// Connected wallet and signed server session are distinct. A signed session
// is required to mine, spend credits and read private assistants/chats.
// The legacy manual address is retained only as a public balance watch address.
// Signing is a plain message: no private key, seed phrase or gas is requested.

import { create } from 'zustand';
import type { BalanceInfo, SessionInfo } from '@kumio/shared';
import { api, ApiError } from '../lib/api';
import { local } from '../lib/storage';
import { useCoordinator } from './coordinator';
import { toast } from './toasts';

interface Eip1193 {
  request(args: { method: string; params?: unknown[] | object }): Promise<unknown>;
  on?(event: string, fn: (...a: unknown[]) => void): void;
  removeListener?(event: string, fn: (...a: unknown[]) => void): void;
}
export interface WalletProvider {
  id: string;
  name: string;
  icon: string | null;
  provider: Eip1193;
}

export const isAddress = (a: unknown): a is string => typeof a === 'string' && /^0x[0-9a-fA-F]{40}$/.test(a);
export const shortAddr = (a: string | null | undefined) => (a ? a.slice(0, 6) + '…' + a.slice(-4) : '');

interface WalletState {
  providers: WalletProvider[];
  providerId: string | null;
  connected: string | null; // account from the wallet
  chainId: number | null;
  manualRecipient: string | null;
  session: SessionInfo | null;
  sessionChecked: boolean;
  balance: BalanceInfo | null;
  balanceStatus: 'idle' | 'loading' | 'ok' | 'offline';
  busy: null | 'connect' | 'sign' | 'switch';
  recipient: () => string | null;
  connect: (providerId?: string) => Promise<void>;
  disconnect: () => Promise<void>;
  setManualRecipient: (a: string | null) => void;
  switchChain: () => Promise<void>;
  signIn: () => Promise<SessionInfo>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  refreshBalance: () => Promise<void>;
  setBalance: (b: BalanceInfo) => void;
}

let activeListeners: { p: Eip1193; acc: (...a: unknown[]) => void; chain: (...a: unknown[]) => void } | null = null;

export const useWallet = create<WalletState>((set, get) => ({
  providers: [],
  providerId: local.get<string | null>('wallet.provider', null),
  connected: null,
  chainId: null,
  manualRecipient: local.get<string | null>('wallet.recipient', null),
  session: null,
  sessionChecked: false,
  balance: null,
  balanceStatus: 'idle',
  busy: null,

  recipient: () => get().connected ?? get().manualRecipient,

  connect: async (providerId) => {
    const s = get();
    const entry = s.providers.find((p) => p.id === (providerId ?? s.providerId)) ?? s.providers[0];
    if (!entry) throw new Error('No browser wallet was found. Install an EVM wallet extension to continue.');
    set({ busy: 'connect' });
    try {
      const accounts = (await entry.provider.request({ method: 'eth_requestAccounts' })) as string[];
      if (!accounts?.[0] || !isAddress(accounts[0])) throw new Error('The wallet did not share an account.');
      const chainHex = (await entry.provider.request({ method: 'eth_chainId' })) as string;
      attach(entry.provider);
      local.set('wallet.provider', entry.id);
      local.set('wallet.wasConnected', true);
      set({ providerId: entry.id, connected: accounts[0], chainId: parseInt(chainHex, 16) });
      // A session for a different address is not valid for this account.
      const sess = get().session;
      if (sess && sess.address.toLowerCase() !== accounts[0].toLowerCase()) await get().logout();
      void get().refreshBalance();
    } catch (e) {
      throw friendly(e);
    } finally {
      set({ busy: null });
    }
  },

  disconnect: async () => {
    detach();
    local.set('wallet.wasConnected', false);
    set({ connected: null, chainId: null });
    await get().logout();
    void get().refreshBalance();
  },

  setManualRecipient: (a) => {
    if (a !== null && !isAddress(a)) throw new Error('That is not a valid EVM address (0x followed by 40 hex characters).');
    local.set('wallet.recipient', a);
    set({ manualRecipient: a });
    void get().refreshBalance();
  },

  switchChain: async () => {
    const cfg = useCoordinator.getState().config;
    const entry = get().providers.find((p) => p.id === get().providerId);
    if (!cfg || !entry) throw new Error('Connect a wallet first.');
    const hex = '0x' + cfg.chain.id.toString(16);
    set({ busy: 'switch' });
    try {
      await entry.provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] });
    } catch (e) {
      if ((e as { code?: number }).code === 4902 && cfg.chain.rpcUrl) {
        await entry.provider.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: hex,
              chainName: cfg.chain.name,
              nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
              rpcUrls: [cfg.chain.rpcUrl],
              blockExplorerUrls: cfg.chain.explorerUrl ? [cfg.chain.explorerUrl] : undefined,
            },
          ],
        });
      } else throw friendly(e);
    } finally {
      set({ busy: null });
    }
  },

  signIn: async () => {
    const s = get();
    const entry = s.providers.find((p) => p.id === s.providerId);
    if (!entry || !s.connected) throw new Error('Connect a browser wallet first. Mining and AI access require a signed-in wallet.');
    set({ busy: 'sign' });
    try {
      const { message } = await api<{ message: string }>(`/v1/auth/nonce?address=${s.connected}&chainId=${s.chainId ?? 0}`);
      // Refuse to sign a message bound to another site, even if a coordinator override asked for it.
      const domain = message.split('\n')[0]?.replace(/ wants you to sign in with your Ethereum account:$/, '');
      if (domain !== location.host) throw new Error(`Refusing to sign: the sign-in message is for "${domain}", but this page is ${location.host}.`);
      if (!message.toLowerCase().includes(s.connected.toLowerCase())) throw new Error('Refusing to sign: the message names a different address.');
      const hex = '0x' + Array.from(new TextEncoder().encode(message), (b) => b.toString(16).padStart(2, '0')).join('');
      const signature = (await entry.provider.request({ method: 'personal_sign', params: [hex, s.connected] })) as string;
      const session = await api<SessionInfo>('/v1/auth/verify', { method: 'POST', json: { message, signature } });
      set({ session });
      void get().refreshBalance();
      return session;
    } catch (e) {
      throw friendly(e);
    } finally {
      set({ busy: null });
    }
  },

  logout: async () => {
    set({ session: null });
    try {
      await api('/v1/auth/logout', { method: 'POST', json: {} });
    } catch {
      /* offline: the cookie expires on its own */
    }
  },

  checkSession: async () => {
    try {
      const session = await api<SessionInfo | null>('/v1/auth/session');
      const c = get().connected;
      if (!session) set({ session: null });
      else if (c && session.address.toLowerCase() !== c.toLowerCase()) {
        await get().logout();
      } else set({ session });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) set({ session: null });
    } finally {
      set({ sessionChecked: true });
    }
  },

  refreshBalance: async () => {
    const addr = get().session?.address ?? get().recipient();
    if (!addr) {
      set({ balance: null, balanceStatus: 'idle' });
      return;
    }
    set({ balanceStatus: get().balance ? get().balanceStatus : 'loading' });
    try {
      const b = await api<BalanceInfo>(`/v1/balance?address=${addr}`);
      const current = get().session?.address ?? get().recipient();
      if (current?.toLowerCase() === addr.toLowerCase()) set({ balance: b, balanceStatus: 'ok' });
    } catch {
      set({ balanceStatus: 'offline' });
    }
  },

  setBalance: (b) => set({ balance: b, balanceStatus: 'ok' }),
}));

function friendly(e: unknown): Error {
  const err = e as { code?: number; message?: string };
  if (err?.code === 4001) return new Error('The request was rejected in the wallet.');
  if (err?.code === -32002) return new Error('The wallet already has a pending request. Open it to continue.');
  if (err?.code === 4100) return new Error('The wallet has not authorised this site. Connect again.');
  return e instanceof Error ? e : new Error(err?.message || 'The wallet returned an error.');
}

function attach(p: Eip1193) {
  detach();
  const acc = (...a: unknown[]) => {
    const list = a[0] as string[];
    const next = list?.[0] && isAddress(list[0]) ? list[0] : null;
    const st = useWallet.getState();
    if (next?.toLowerCase() === st.connected?.toLowerCase()) return;
    useWallet.setState({ connected: next });
    if (st.session && (!next || st.session.address.toLowerCase() !== next.toLowerCase())) {
      void st.logout();
      toast('Wallet account changed. Sign in again to mine or use credits.', 'info');
    }
    void useWallet.getState().refreshBalance();
  };
  const chain = (...a: unknown[]) => useWallet.setState({ chainId: parseInt(String(a[0]), 16) });
  p.on?.('accountsChanged', acc);
  p.on?.('chainChanged', chain);
  activeListeners = { p, acc, chain };
}
function detach() {
  if (!activeListeners) return;
  activeListeners.p.removeListener?.('accountsChanged', activeListeners.acc);
  activeListeners.p.removeListener?.('chainChanged', activeListeners.chain);
  activeListeners = null;
}

/** EIP-6963 discovery plus a legacy window.ethereum fallback. Never prompts. */
export function discoverWallets() {
  const add = (w: WalletProvider) =>
    useWallet.setState((s) => (s.providers.some((p) => p.id === w.id) ? s : { providers: [...s.providers, w] }));
  window.addEventListener('eip6963:announceProvider', (ev: Event) => {
    const d = (ev as CustomEvent).detail as { info: { uuid: string; name: string; icon: string; rdns: string }; provider: Eip1193 };
    if (d?.info && d.provider) add({ id: d.info.rdns || d.info.uuid, name: d.info.name, icon: d.info.icon?.startsWith('data:image/') ? d.info.icon : null, provider: d.provider });
  });
  window.dispatchEvent(new Event('eip6963:requestProvider'));
  setTimeout(() => {
    const eth = (window as unknown as { ethereum?: Eip1193 }).ethereum;
    if (eth && !useWallet.getState().providers.length) add({ id: 'injected', name: 'Browser wallet', icon: null, provider: eth });
    // Re-attach silently if the user connected before (eth_accounts does not prompt).
    const st = useWallet.getState();
    const entry = st.providers.find((p) => p.id === st.providerId);
    if (entry && local.get('wallet.wasConnected', false)) {
      entry.provider
        .request({ method: 'eth_accounts' })
        .then(async (acc) => {
          const a = (acc as string[])[0];
          if (!isAddress(a)) return;
          const chainHex = (await entry.provider.request({ method: 'eth_chainId' })) as string;
          attach(entry.provider);
          useWallet.setState({ connected: a, chainId: parseInt(chainHex, 16) });
          void useWallet.getState().checkSession();
          void useWallet.getState().refreshBalance();
        })
        .catch(() => {});
    }
  }, 400);
}

let balTimer: ReturnType<typeof setInterval> | null = null;
export function startBalancePolling() {
  if (balTimer) return;
  void useWallet.getState().checkSession();
  void useWallet.getState().refreshBalance();
  balTimer = setInterval(() => !document.hidden && void useWallet.getState().refreshBalance(), 30000);
}
